import { useState } from 'react';
import { Alert, Button } from '../../components/ui';
import type { BillRunCoverage } from '../../api/types';

// ---------------------------------------------------------------------------
// TV-3 — what the operator sees when a bill run would skip meters.
//
// PRESENTATION, from the owner verbatim: a per-meter list "on a bill run that
// has say 100 meters could go off screen and turn into a mess". So the counts
// and the remedies are always visible, and the per-meter detail is behind a
// disclosure that is closed by default.
//
// THE COPY IS NOT WRITTEN HERE. `label` and `remedy` arrive per reason from the
// endpoint, which takes them from bill-calculator's own SKIP_REASON_LABELS /
// SKIP_REASON_REMEDIES. That matters because the remedies genuinely differ —
// "create the meter configuration, then assign a tariff" versus "add a tariff
// version covering this period, or extend the current one" — and the previous
// attempt at this guard conflated the two states and printed a remedy that could
// not work. A copy of the strings here would be a fourth place for them to
// drift.
//
// LAYOUT NOTES (375px; ~341px usable inside the page gutter, and less again
// inside the dialog):
//   - Every row is a flex item with `min-w-0` ON THE ITEM, because flex items
//     default to `min-width: auto` and refuse to shrink below their widest
//     descendant. A meter name or a tariff name is exactly the unbounded token
//     that turns into a floor for the whole column.
//   - Nothing here is `whitespace-nowrap`. Names use `break-words`; the day
//     ranges are short enough to wrap at the arrow.
//   - The disclosure toggle is a `<Button>`, which carries `.tap-target`
//     already — never a bare `min-h-tap`.
//   - The chevron swaps its `d` rather than using `rotate-180`. A conditional
//     `rotate-*` compiles to a `transform`, which creates a stacking context;
//     that is what floated a chevron above a click overlay in the tariff card
//     and made it uncollapsible in the expanded state only.
//   - Tones are `Alert`'s `danger` and `warning`, whose pairs are already
//     asserted in src/test/contrast.test.ts in both themes. No new colour pair
//     is introduced, so nothing here rests on an unverified shade.
// ---------------------------------------------------------------------------

/** A static chevron. `d` swaps; no transform, so no stacking context. */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={open ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
      />
    </svg>
  );
}

/** `[start, end)` as the days an operator has to fill. End is exclusive. */
function formatSpans(spans: BillRunCoverage['blocked'][number]['uncovered']): string {
  return spans.map((s) => `${s.start} → ${s.end}`).join(', ');
}

export function CoverageBlockNotice({
  coverage,
  rechecking,
}: {
  coverage: BillRunCoverage;
  /** A newer check is in flight; the block below is the last known answer. */
  rechecking: boolean;
}) {
  const [showMeters, setShowMeters] = useState(false);
  const { meters_blocked, meters_in_scope, by_reason, blocked_pockets, blocked } = coverage;

  return (
    <Alert tone="danger" className="mt-3">
      <div className="min-w-0 space-y-2">
        {/* "meters this run would price", not "selected meters" — deliberately
            NOT the same number as the "N pockets selected (M meters)" line
            above. That line counts every meter attached to the pockets;
            `meters_in_scope` counts only the ones the calculator would actually
            price, which excludes common-area meters (billed through
            allocations) and any pocket meter with no OpenRemote asset. Saying
            "selected" here would present a smaller number as a count of the
            same thing and read as an arithmetic bug. */}
        <p className="font-semibold">
          {meters_blocked} of {meters_in_scope} meter{meters_in_scope === 1 ? '' : 's'} this run
          would price cannot be billed for {coverage.period_start} → {coverage.period_end}
        </p>
        <p className="text-xs">
          The calculator refuses to price a meter with no tariff version for the period, and
          will not substitute another tariff. Fix the tariff, or deselect the pocket.
        </p>

        {/* Summary by reason — the count and the remedy, which differ per reason. */}
        <ul className="text-xs space-y-1.5">
          {by_reason.map((r) => (
            <li key={r.reason} className="min-w-0">
              <span className="font-semibold">
                {r.count} meter{r.count === 1 ? '' : 's'}
              </span>
              {' — '}
              <span className="break-words">{r.label}</span>
              <span className="block opacity-90 break-words">Fix: {r.remedy}</span>
            </li>
          ))}
        </ul>

        {blocked_pockets.length > 0 && (
          <p className="text-xs min-w-0 break-words">
            <span className="font-semibold">Pockets affected:</span>{' '}
            {blocked_pockets.map((p) => `${p.pocket_name} (${p.blocked_count})`).join(', ')}
          </p>
        )}

        {/* Only ever rendered when the server VERIFIED that this end clears
            every block. A shortening that would not work is never suggested. */}
        {coverage.suggested_period_end !== null && (
          <p className="text-xs min-w-0 break-words">
            <span className="font-semibold">A period ending {coverage.suggested_period_end}</span>{' '}
            would cover every selected meter.
          </p>
        )}

        {rechecking && <p className="text-xs opacity-80">Re-checking the new selection…</p>}

        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowMeters((v) => !v)}
          aria-expanded={showMeters}
        >
          <Chevron open={showMeters} />
          {showMeters ? 'Hide meters' : `Show the ${meters_blocked} meter${meters_blocked === 1 ? '' : 's'}`}
        </Button>

        {showMeters && (
          <ul className="text-xs space-y-2">
            {blocked.map((m) => (
              <li key={`${m.pocket_id}:${m.meter_asset_id}`} className="min-w-0">
                <span className="font-semibold break-words">{m.meter_name}</span>
                <span className="block opacity-90 break-words">
                  {m.pocket_name}
                  {m.tariff_name !== null ? ` · ${m.tariff_name}` : ''}
                </span>
                <span className="block opacity-90 break-words">
                  {m.reason_label}
                </span>
                {m.uncovered.length > 0 && (
                  <span className="block opacity-90 break-words">
                    Unpriced days: {formatSpans(m.uncovered)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Alert>
  );
}

/**
 * The endpoint could not answer.
 *
 * Deliberately does NOT block Create — see useCoverageGuard's decision 2. The
 * copy says what is unknown and what still protects the run, so permitting is
 * not silent.
 */
export function CoverageUnavailableNotice({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Alert tone="warning" className="mt-3">
      <div className="min-w-0 space-y-2">
        <p className="font-semibold">Could not check tariff coverage</p>
        <p className="text-xs break-words">
          {message}. The run is not blocked — the calculator still refuses to price any meter
          without a tariff version and flags the run for review, so nothing is billed at a
          substituted rate.
        </p>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Check again
        </Button>
      </div>
    </Alert>
  );
}
